import { PenIcon, TrashAltIcon } from "assets/icons";
import { If, Media } from "components";
import { useBoolean, useDebouncedValue } from "hooks";
import { ProductsSelectorTable } from "modules/products";
import { useMemo, useState } from "react";
import { Button, Loader, Modal, SearchInput } from "ui";

import { useProductSetDetails, useUpdateProductSet } from "../../../apis";
import { ProductSetFormShowOnField } from "../../forms";
import { DeleteProductSetModal } from "../DeleteProductSetModal";
import { ProductSetFormModal } from "../ProductSetFormModal";
import { ProductSetModalProps } from "./ProductSetDetailsModal.types";

import styles from "./ProductSetDetailsModal.module.scss";

const PRODUCT_SET_FORM_ID = "feed-insert-form-id";

const ProductSetDetailsModal = ({
  isOpen,
  onClose,
  setId,
  withControls = true
}: ProductSetModalProps) => {
  const { productSetDetails, isProductSetDetailsLoading } =
    useProductSetDetails(setId);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 500);

  const {
    value: isOpenProductSetFormModal,
    setTrue: onOpenProductSetFormModal,
    setFalse: onCloseProductSetFormModal
  } = useBoolean();

  const {
    value: isOpenDeleteProductSetModal,
    setTrue: onOpenDeleteProductSetModal,
    setFalse: onCloseDeleteProductSetModal
  } = useBoolean();

  const updateProductSet = useUpdateProductSet();

  const productsList = useMemo(() => {
    return (
      productSetDetails?.productVariants
        .filter((product) =>
          product.productVariant.name
            .toLowerCase()
            .includes(debouncedSearch.toLowerCase())
        )
        .map((product) => product.productVariant) ?? []
    );
  }, [productSetDetails?.productVariants, debouncedSearch]);

  if (!setId) return null;

  return (
    <Modal
      formId={PRODUCT_SET_FORM_ID}
      isOpen={isOpen}
      title={productSetDetails?.title ?? ""}
      footer={
        withControls ? (
          <div className={styles.footer}>
            <Button
              onClick={onOpenDeleteProductSetModal}
              variant="delete"
              icon={<TrashAltIcon width="20" height="20" fill="#FFF" />}
            >
              Delete
            </Button>

            <Button
              onClick={onOpenProductSetFormModal}
              variant="default"
              icon={<PenIcon width="20" height="20" />}
            >
              Edit
            </Button>
          </div>
        ) : (
          <></>
        )
      }
      onClose={onClose}
    >
      {isProductSetDetailsLoading && <Loader />}

      {productSetDetails && (
        <div className={styles.content}>
          <span>{productSetDetails.description}</span>

          <ProductSetFormShowOnField
            title="Show on Home"
            description="When enabled, the banner will appear in the slider on the Home page of the app."
            value={productSetDetails.showOnHome}
            disabled={!withControls}
            onChange={(e) =>
              updateProductSet.mutate({
                id: setId,
                showOnHome: e.target.checked
              })
            }
          />

          <ProductSetFormShowOnField
            title="Show in Search"
            description="When enabled, the banner will appear in the search results."
            value={productSetDetails.showInSearch}
            disabled={!withControls}
            onChange={(e) =>
              updateProductSet.mutate({
                id: setId,
                showInSearch: e.target.checked
              })
            }
          />

          <Media
            mediaFile={productSetDetails.coverMedia}
            className={styles.coverMedia}
            controls
          />

          <SearchInput value={search} onChange={setSearch} />

          <If condition={productSetDetails.productVariants.length > 0}>
            <ProductsSelectorTable products={productsList} />
          </If>
        </div>
      )}

      <ProductSetFormModal
        isOpen={isOpenProductSetFormModal}
        onClose={onCloseProductSetFormModal}
        editableProductSetId={setId}
      />

      <DeleteProductSetModal
        isOpen={isOpenDeleteProductSetModal}
        id={setId}
        onClose={onCloseDeleteProductSetModal}
        onDeleteSuccess={onClose}
      />
    </Modal>
  );
};

export default ProductSetDetailsModal;
